import React from 'react';
import { Body, Button, Dropdown, Flex, Icon, Menu } from '@cognite/cogs.js';
import styled from 'styled-components/macro';
import { DigitalTwin } from 'types/dt-graphql-apollo-v2';
import { InfoMessage } from 'components/InfoMessage/InfoMessage';
import { useStatefulHistory } from 'hooks/history';
import { useUrl } from 'hooks/useUrls';
import { getConsistencyReportUrl } from 'utils/router-url';
import {
  useConsistencyReports,
  REPORT_SORT_OPTIONS,
} from '../hooks/useConsistencyReports';
import { ConsistencyReportCard } from './ConsistencyReportCard';

export const ConsistencyReports = ({
  digitalTwin,
}: {
  digitalTwin: DigitalTwin;
}) => {
  const history = useStatefulHistory();
  const tenant = useUrl.Tenant();

  const { reports, setSortOption, selectedSortLabel } = useConsistencyReports(
    digitalTwin.externalId
  );

  const sortingDisabled = reports.length === 0;

  if (reports.length === 0) {
    return (
      <InfoMessage type="Search" title="No consistency reports">
        No consistency reports were found.
      </InfoMessage>
    );
  }

  return (
    <>
      <SortRow alignItems="flex-end" direction="column">
        <Dropdown
          content={
            <Menu>
              {REPORT_SORT_OPTIONS.map((option) => (
                <Menu.Item
                  key={option.value}
                  onClick={() => setSortOption(option.value)}
                >
                  <Flex
                    alignItems="center"
                    gap={8}
                    justifyContent="space-between"
                  >
                    <span>{option.name}</span>
                    <Icon type={option.icon} />
                  </Flex>
                </Menu.Item>
              ))}
            </Menu>
          }
          disabled={sortingDisabled}
        >
          <Button
            disabled={sortingDisabled}
            icon="ReorderDefault"
            iconPlacement="right"
          >
            Sort
          </Button>
        </Dropdown>
        <Body size="small">{selectedSortLabel}</Body>
      </SortRow>

      <StyledList wrap="wrap" gap={42}>
        {reports.map((report) => (
          <ConsistencyReportCard
            key={report.reportId}
            report={report}
            onOpen={() =>
              history.pushStateful(
                getConsistencyReportUrl(
                  tenant,
                  digitalTwin.externalId,
                  report.reportId
                )
              )
            }
          />
        ))}
      </StyledList>
    </>
  );
};

const SortRow = styled(Flex)`
  padding: 0 2rem;

  .cogs-body--small {
    color: var(--cogs-text-color-secondary);
  }
`;

const StyledList = styled(Flex)`
  overflow: auto;
  padding: 1rem 2rem;
  margin-bottom: 2rem;
`;
